import type { ReactNode } from 'react';
import { Check, Lock } from 'lucide-react';

export interface StepperStep {
  id: string;
  label: string;
  icon: ReactNode;
  status: 'completed' | 'active' | 'pending' | 'locked';
  completedAt?: string | null;
}

interface StepperNavProps {
  steps: StepperStep[];
  activeStep: number;
  onStepClick?: (index: number) => void;
  className?: string;
}

export function StepperNav({
  steps,
  activeStep,
  onStepClick,
  className = '',
}: StepperNavProps) {
  return (
    <div className={className}>
      {/* Desktop: horizontal */}
      <div className="tw-hidden sm:tw-flex tw-items-center tw-justify-between tw-overflow-x-auto tw-scrollbar-hide">
        {steps.map((step, i) => {
          const isClickable = step.status !== 'locked' && onStepClick;
          const isActive = i === activeStep;
          const isCompleted = step.status === 'completed';
          const isLocked = step.status === 'locked';

          return (
            <div key={step.id} className="tw-flex tw-items-center tw-flex-1 tw-min-w-0 last:tw-flex-none">
              {/* Step */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick(i)}
                disabled={isLocked}
                className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-transition-all tw-whitespace-nowrap ${
                  isActive
                    ? 'tw-bg-affinicar-primary/10 tw-text-affinicar-primary tw-font-semibold'
                    : isCompleted
                      ? 'tw-text-green-700 hover:tw-bg-green-50 tw-cursor-pointer'
                      : isLocked
                        ? 'tw-text-gray-300 tw-cursor-not-allowed'
                        : 'tw-text-gray-500 hover:tw-bg-gray-50 tw-cursor-pointer'
                }`}
              >
                {/* Icon circle */}
                <span
                  className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-text-xs tw-font-bold ${
                    isActive
                      ? 'tw-bg-affinicar-primary tw-text-white tw-shadow-sm tw-ring-2 tw-ring-affinicar-primary/30'
                      : isCompleted
                        ? 'tw-bg-green-500 tw-text-white'
                        : isLocked
                          ? 'tw-bg-gray-100 tw-text-gray-300'
                          : 'tw-bg-gray-200 tw-text-gray-500'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="tw-w-3.5 tw-h-3.5" />
                  ) : isLocked ? (
                    <Lock className="tw-w-3 tw-h-3" />
                  ) : (
                    <span className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center tw-justify-center">
                      {step.icon}
                    </span>
                  )}
                </span>

                <div className="tw-flex tw-flex-col">
                  <span className="tw-text-sm">{step.label}</span>
                  {isCompleted && step.completedAt && (
                    <span className="tw-hidden md:tw-block tw-text-[10px] tw-text-green-600 tw-font-normal">
                      Terminée le {new Date(step.completedAt).toLocaleDateString('fr-FR')} à {new Date(step.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </button>

              {/* Connector */}
              {i < steps.length - 1 && (
                <div className="tw-flex-1 tw-mx-1 tw-min-w-[16px]">
                  <div
                    className={`tw-h-0.5 tw-rounded-full ${
                      isCompleted ? 'tw-bg-green-400' : isActive ? 'tw-bg-affinicar-primary/30' : 'tw-bg-gray-200'
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: vertical compact */}
      <div className="sm:tw-hidden tw-space-y-1">
        {steps.map((step, i) => {
          const isActive = i === activeStep;
          const isCompleted = step.status === 'completed';
          const isLocked = step.status === 'locked';
          const isClickable = !isLocked && onStepClick;

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isClickable && onStepClick(i)}
              disabled={isLocked}
              className={`tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-rounded-lg tw-transition-colors ${
                isActive
                  ? 'tw-bg-affinicar-primary/10 tw-text-affinicar-primary tw-font-semibold'
                  : isCompleted
                    ? 'tw-text-green-700'
                    : isLocked
                      ? 'tw-text-gray-300'
                      : 'tw-text-gray-500'
              }`}
            >
              <span
                className={`tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-text-xs ${
                  isActive
                    ? 'tw-bg-affinicar-primary tw-text-white'
                    : isCompleted
                      ? 'tw-bg-green-500 tw-text-white'
                      : isLocked
                        ? 'tw-bg-gray-100 tw-text-gray-300'
                        : 'tw-bg-gray-200 tw-text-gray-500'
                }`}
              >
                {isCompleted ? <Check className="tw-w-3 tw-h-3" /> : isLocked ? <Lock className="tw-w-2.5 tw-h-2.5" /> : (i + 1)}
              </span>
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-sm">{step.label}</span>
                {isCompleted && step.completedAt && (
                  <span className="tw-text-[10px] tw-text-green-600 tw-font-normal">
                    Terminée le {new Date(step.completedAt).toLocaleDateString('fr-FR')} à {new Date(step.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
